/**
 * useImportOwners — the option set for the "Imported by" scope menu.
 *
 * Deliberately parameterless. The options used to be derived from whichever
 * page of the imports list happened to be loaded (`deriveOwners(items)`), which
 * made them a function of the tag chip, the search text, the page size and the
 * owner scope itself. Three consequences, all observed on dev:
 *
 *   - picking a tag chip dropped every recruiter with no row on that page;
 *   - picking a recruiter left only that recruiter in the menu, so there was no
 *     way to switch straight to another one — you had to go via "Everyone";
 *   - with more imports than the page size, recruiters further down the list
 *     never appeared as options at all.
 *
 * The list is firm-wide and changes only when someone's first import lands, so
 * it is cached hard rather than refetched alongside the list.
 */

import type { ImportOwnersResponse } from "@nu-assist/contracts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import type { ImportsClient } from "../../../clients/imports.client";
import type { OwnerOption } from "./ImportOwnerMenu";

export interface UseImportOwnersArgs {
  client: ImportsClient;
}

/** A new recruiter only enters the set on their first-ever import. */
const STALE_TIME_MS = 5 * 60 * 1000;

/** Wire shape (`uploader_email` / `owner_name`, matching `ImportSummary`) →
 *  the menu's `OwnerOption`. Mapped here so neither consumer re-implements it —
 *  the two hand-rolled `deriveOwners` copies this replaced had already drifted
 *  from each other. */
function toOwnerOptions(res: ImportOwnersResponse): OwnerOption[] {
  return res.items.map((o) => ({ email: o.uploader_email, name: o.owner_name ?? null }));
}

export interface UseImportOwnersResult {
  query: UseQueryResult<OwnerOption[], Error>;
  /**
   * Menu-ready owners: `null` while the request is in flight, `[]` once it has
   * answered (including on failure, where the caller's error surface carries
   * the meaning).
   *
   * Derived HERE rather than at each call site — the distinction is the whole
   * point of the endpoint, and a consumer that writes `data ?? []` by hand
   * silently renders the pending menu as "no colleague has imports", which is
   * exactly the misreading this replaced. Same shape as `useSearchReport`,
   * which returns its query alongside the values derived from it.
   */
  owners: readonly OwnerOption[] | null;
}

export function useImportOwners({ client }: UseImportOwnersArgs): UseImportOwnersResult {
  const query = useQuery<ImportOwnersResponse, Error, OwnerOption[]>({
    // No filter values in the key — that absence is the whole point.
    queryKey: ["imports", "owners"],
    queryFn: () => client.listImportOwners(),
    select: toOwnerOptions,
    staleTime: STALE_TIME_MS,
  });

  return { query, owners: query.isPending ? null : (query.data ?? []) };
}
